import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef } from 'react';
import getComponentName from 'react-utils/getComponentName';
import { useChecked, checkedPropTypes } from '../hooks/useChecked';
import { attachWrappedComponent } from './utils';

/**
 * Similar to `Controllable`, but specifically for controlling a `checked` property using the same
 * API as native checkbox inputs: `checked`, `defaultChecked`, and `onChange`. Additionally,
 * the events passed to `onChange` use `evt.target.checked` instead of `evt.target.value`.
 *
 * This should not be used with radio inputs, as their `checked` state is dependant on the
 * others inputs in the group (see UIComponents#9441).
 *
 * @param {JSX.Component} Component
 * @returns {JSX.Component} A component with defaultable `checked` prop
 */
export default function Checkable(Component) {
  const CheckableComponent = /*#__PURE__*/forwardRef((props, ref) => {
    const rest = _objectWithoutPropertiesLoose(props, ["checked", "defaultChecked", "onChange"]); // eslint-disable-next-line react-hooks/rules-of-hooks


    const {
      checked,
      onChange
    } = useChecked(props);
    return /*#__PURE__*/_jsx(Component, Object.assign({}, rest, {
      checked: checked,
      onChange: onChange,
      ref: ref
    }));
  });
  CheckableComponent.propTypes = Object.assign({}, Component.propTypes, {}, checkedPropTypes);
  CheckableComponent.defaultProps = Object.assign({}, Component.defaultProps, {
    defaultChecked: Component.defaultProps && Component.defaultProps.checked != null ? Component.defaultProps.checked : false
  });
  delete CheckableComponent.defaultProps.checked;
  CheckableComponent.displayName = `Checkable(${getComponentName(Component)})`;
  attachWrappedComponent(CheckableComponent, Component);
  return CheckableComponent;
}